/**
 * Route Registry — single source of truth for pane routes, neutral routes,
 * and nav section data. Prevents pane detection from defaulting to 'product'
 * when navigating to non-pane routes like /to, /se, /ta.
 */
import type { PaneType } from './contexts/PaneContext';

// ── Pane routes: URL prefix → pane mapping ───────────────────────────────

export interface PaneRoute {
  prefix: string;
  pane: PaneType;
  projectScoped: boolean;
}

export const PANE_ROUTES: PaneRoute[] = [
  { prefix: 'product', pane: 'product', projectScoped: true },
  { prefix: 'archie', pane: 'archie', projectScoped: false },
  { prefix: 'sales', pane: 'sales', projectScoped: false },
  { prefix: 'success', pane: 'success', projectScoped: false },
  { prefix: 'operations', pane: 'ops', projectScoped: false },
  { prefix: 'docs', pane: 'documentation', projectScoped: false },
];

// Legacy prefix mapping for backward-compatible redirects
export const LEGACY_PREFIX_MAP: Record<string, string> = {
  pr: 'product',
  ar: 'archie',
  sm: 'sales',
  su: 'success',
  op: 'operations',
  dc: 'docs',
  se: 'settings',
  ta: 'tasks',
  to: 'topics',
};

// ── Neutral routes: don't belong to any pane ─────────────────────────────

export interface NeutralRoute {
  prefix: string;
}

export const NEUTRAL_ROUTES: NeutralRoute[] = [
  { prefix: 'topics' },  // Topic workspace
  { prefix: 'settings' },  // Settings
  { prefix: 'tasks' },  // Tasks page
];

// ── Precomputed lookup structures ────────────────────────────────────────

export const PREFIX_TO_PANE = new Map<string, PaneType>(
  PANE_ROUTES.map(r => [r.prefix, r.pane])
);

export const NEUTRAL_PREFIXES = new Set<string>(
  NEUTRAL_ROUTES.map(r => r.prefix)
);

export const WORKSPACE_SCOPED_PREFIXES = new Set<string>(
  PANE_ROUTES.filter(r => !r.projectScoped).map(r => r.prefix)
);

/**
 * Resolves a URL prefix to a pane type.
 * Returns null for neutral routes (topic, settings, tasks) — caller should
 * preserve the current pane instead of defaulting.
 */
export function resolvePrefixToPane(prefix: string): PaneType | null {
  if (NEUTRAL_PREFIXES.has(prefix)) return null;
  return PREFIX_TO_PANE.get(prefix) ?? null;
}

// ── Nav section data (labels + paths, no icons) ──────────────────────────

export interface NavSectionData {
  title: string;
  items: NavItemData[];
}

export interface NavItemData {
  path: string;
  label: string;
  projectScoped?: boolean;
}

// ── Chat hints: route-aware prompts for the Martha sidebar ──────────────
// Keyed by "{pane}/{path}" (e.g. "archie/pg"). Falls back to pane-level then global.

export const ROUTE_CHAT_HINTS: Record<string, string[]> = {
  // Design Studio & Playground — ADL composition prompts
  'archie/design-studio': [
    'Design a settings page with theme, notifications, and billing sections',
    'Add a KpiStrip showing revenue, profit margin, and headcount',
    'Create a financial table with monthly P&L breakdown',
    'Build a report layout with header, KPI strip, and data table',
    'Add a settings nav with General, Security, Integrations, and Billing items',
    'Design a client dashboard with engagement score, recent activity, and contacts',
    'Build a form with text input, select, toggle, and submit button',
    'Design a card layout with avatar, name, role, and action buttons',
  ],
  'archie/playground': [
    'Design a settings page with theme, notifications, and billing sections',
    'Add a KpiStrip showing revenue, profit margin, and headcount',
    'Create a financial table with monthly P&L breakdown',
    'Build a report layout with header, KPI strip, and data table',
    'Design an onboarding wizard with 4 steps: Welcome, Profile, Team, Done',
    'Add a filter bar with date range, status dropdown, and search',
    'Create an empty state with illustration placeholder and CTA button',
    'Add a status pill grid showing all service health indicators',
  ],
  // Product pane
  'product/documents': [
    'Summarise this document',
    'Generate a specification from this requirements doc',
    'Find all linked issues for this document',
  ],
  'product/boards': [
    'Show all blocked issues on this board',
    'What epics are behind schedule?',
    'Create a new issue for this epic',
  ],
  'product/sprints': [
    'Check sprint readiness — are all specs linked to tests?',
    'What stories are still unestimated?',
    'Generate a sprint plan from the backlog',
  ],
  'product/epics': [
    'Show the status of all epics in this project',
    'Which epics are behind schedule?',
    'Summarise the stories under this epic',
  ],
};

// Pane-level fallback hints (when no route-specific hints match)
export const PANE_CHAT_HINTS: Record<PaneType, string[]> = {
  archie: [
    'Check the status of the latest fine-tuning run',
    'Show agent error rates for the last 24 hours',
    'What regulatory feeds have new content?',
  ],
  product: [
    'Generate a specification from this requirements doc',
    'Show all blocked issues on this board',
    'Check build readiness — are all specs linked to tests?',
  ],
  sales: [
    'Draft a follow-up email for the last meeting',
    'Show deal pipeline summary',
    'Prep a briefing for my next call',
  ],
  success: [
    'Show customers with declining health scores',
    'Draft a check-in email for at-risk accounts',
    'What support tickets are overdue?',
  ],
  ops: [
    'Show current month spend vs budget',
    'List pending policy reviews',
    'Who joined the team this month?',
  ],
};

/**
 * Resolve chat hints for a given pathname.
 * Checks route-specific → pane-level → global fallback.
 */
export function getChatHintsForPath(pathname: string): string[] {
  // Extract pane prefix and route segment from path like /w/archie/ar/pg/...
  const segments = pathname.split('/').filter(Boolean);
  // Find pane prefix (ar, sm, pr, etc.) and the route segment after it
  const paneIdx = segments.findIndex(s => PREFIX_TO_PANE.has(s));
  if (paneIdx >= 0) {
    const panePrefix = segments[paneIdx];
    const pane = PREFIX_TO_PANE.get(panePrefix);
    const routeSegment = segments[paneIdx + 1];

    // Try route-specific hints first
    if (pane && routeSegment) {
      const key = `${pane}/${routeSegment}`;
      if (ROUTE_CHAT_HINTS[key]) return ROUTE_CHAT_HINTS[key];
    }

    // Fall back to pane-level hints
    if (pane && PANE_CHAT_HINTS[pane]) return PANE_CHAT_HINTS[pane];
  }

  return [];
}

export const PANE_NAV_SECTIONS: Record<PaneType, NavSectionData[]> = {
  product: [
    {
      title: 'DESIGN',
      items: [
        { path: '/', label: 'Overview' },
        { path: 'documents', label: 'Documentation', projectScoped: true },
        { path: 'trash', label: 'Trash', projectScoped: true },
      ],
    },
    {
      title: 'PLANNING',
      items: [
        { path: 'boards', label: 'Boards', projectScoped: true },
        { path: 'sprints', label: 'Sprint Prep', projectScoped: true },
        { path: 'build-config', label: 'Build Config', projectScoped: true },
      ],
    },
    {
      title: 'DEVELOPMENT',
      items: [
        { path: 'queue', label: 'Queue', projectScoped: true },
        { path: 'monitor', label: 'Monitor', projectScoped: true },
        { path: 'testing', label: 'Testing', projectScoped: true },
        { path: 'staging', label: 'Staging', projectScoped: true },
      ],
    },
    {
      title: 'SETTINGS',
      items: [
        { path: 'environment', label: 'Environment', projectScoped: true },
        { path: '/api/improvement/docs', label: 'API Reference' },
      ],
    },
  ],
  success: [
    {
      title: 'CUSTOMER SUCCESS',
      items: [
        { path: 'customers', label: 'Customers' },
        { path: 'users', label: 'Users' },
      ],
    },
    {
      title: 'SUPPORT',
      items: [
        { path: 'tickets', label: 'Support Tickets' },
        { path: 'inbox', label: 'Inbox' },
        { path: 'knowledge-base', label: 'Knowledge Base' },
      ],
    },
    {
      title: 'ANALYTICS',
      items: [
        { path: 'analytics', label: 'Content Analytics' },
      ],
    },
    {
      title: 'PILOT TOOLS',
      items: [
        { path: 'anonymiser', label: 'Anonymiser' },
        { path: 'sop-generator', label: 'SOP Generator' },
      ],
    },
  ],
  sales: [
    {
      title: 'CONTENT',
      items: [
        { path: 'content', label: 'All Content' },
        { path: 'linkedin', label: 'LinkedIn Posts' },
        { path: 'blog', label: 'Blog Posts' },
        { path: 'calendar', label: 'Calendar' },
        { path: 'templates', label: 'Templates' },
        { path: 'contributors', label: 'Contributors' },
      ],
    },
    {
      title: 'CRM',
      items: [
        { path: 'contacts', label: 'Contacts' },
        { path: 'companies', label: 'Companies' },
        { path: 'deals', label: 'Deals' },
        { path: 'pipeline', label: 'Pipeline' },
      ],
    },
    {
      title: 'COMMUNICATIONS',
      items: [
        { path: 'inbox', label: 'Inbox' },
        { path: 'meetings', label: 'Meetings' },
        { path: 'my-calendar', label: 'My Calendar' },
      ],
    },
    {
      title: 'WEBSITE',
      items: [
        { path: 'landing-pages', label: 'Landing Pages' },
        { path: 'website-pages', label: 'Website Pages' },
      ],
    },
    {
      title: 'SETTINGS',
      items: [
        { path: 'distribution', label: 'Distribution' },
        { path: 'brand-voice', label: 'Brand Voice' },
        { path: 'media', label: 'Media Library' },
        { path: 'cms-settings', label: 'CMS Settings' },
      ],
    },
  ],
  ops: [
    {
      title: 'FINANCE',
      items: [
        { path: 'costs', label: 'Costs' },
      ],
    },
    {
      title: 'GOVERNANCE',
      items: [
        { path: 'policies', label: 'Policies' },
      ],
    },
    {
      title: 'AUTOMATION',
      items: [
        { path: 'triggers', label: 'Triggers' },
        { path: 'scheduler', label: 'Scheduler' },
        { path: 'topics', label: 'Topics' },
      ],
    },
  ],
  archie: [
    {
      title: 'TRAINING',
      items: [
        { path: 'pipelines', label: 'Pipeline Runs' },
        { path: 'models', label: 'Model Registry' },
        { path: 'datasets', label: 'Datasets' },
        { path: 'quality-gates', label: 'Quality Gates' },
        { path: 'evals', label: 'Eval Results' },
      ],
    },
    {
      title: 'LEARNING',
      items: [
        { path: 'feedback', label: 'Feedback Loops' },
        { path: 'corrections', label: 'Corrections' },
        { path: 'signals', label: 'Signals' },
      ],
    },
    {
      title: 'AGENTS',
      items: [
        { path: 'sessions', label: 'Sessions' },
        { path: 'fleet', label: 'Agent Fleet' },
        { path: 'catalog', label: 'Agent Catalog' },
        { path: 'coordination', label: 'Coordination' },
        { path: 'playbooks', label: 'Playbooks' },
        { path: 'triggers', label: 'Triggers' },
      ],
    },
    {
      title: 'OBSERVABILITY',
      items: [
        { path: 'dashboard', label: 'Dashboard' },
        { path: 'logs', label: 'LLM Logs' },
        { path: 'audit', label: 'Audit Trail' },
        { path: 'development', label: 'Development' },
      ],
    },
    {
      title: 'SKILLS',
      items: [
        { path: 'skills', label: "Archie's Skills" },
      ],
    },
    {
      title: 'KNOWLEDGE',
      items: [
        { path: 'firm-knowledge', label: 'Firm Knowledge' },
        { path: 'clients', label: 'Clients' },
      ],
    },
    {
      title: 'ALWAYS-ON',
      items: [
        { path: 'martha-mind', label: 'Martha Mind' },
      ],
    },
    {
      title: 'INFRASTRUCTURE',
      items: [
        { path: 'regulatory-feeds', label: 'Regulatory Feeds' },
        { path: 'scheduler', label: 'Task Scheduler' },
        { path: 'presence', label: 'Presence Fleet' },
      ],
    },
    {
      title: 'DEMO',
      items: [
        { path: 'demo', label: 'Demo Environments' },
        { path: 'staging', label: 'Staging' },
      ],
    },
    {
      title: 'DESIGN',
      items: [
        { path: 'design-studio', label: 'Design Studio' },
        { path: 'playground', label: 'Playground' },
      ],
    },
    {
      title: 'CONNECT',
      items: [
        { path: 'connect/orgs', label: 'Orgs' },
        { path: 'connect/events', label: 'Events' },
        { path: 'connect/keys', label: 'API Keys' },
        { path: 'connect/usage', label: 'Usage' },
        { path: 'connect/endpoints', label: 'Endpoint Registry' },
      ],
    },
  ],
  documentation: [
    {
      title: 'WORKSPACE',
      items: [
        { path: '/', label: 'Hub' },
        { path: 'search', label: 'Search' },
      ],
    },
    {
      title: 'AREAS',
      items: [
        { path: 'product', label: 'Product' },
        { path: 'success', label: 'Success & Support' },
        { path: 'sales', label: 'Sales & Marketing' },
        { path: 'ops', label: 'Operations' },
        { path: 'archie', label: 'Archie' },
      ],
    },
    {
      title: 'MANAGE',
      items: [
        { path: 'templates', label: 'Templates' },
        { path: 'trash', label: 'Trash' },
      ],
    },
  ],
};
